import React from "react";
import { Link } from "react-router-dom";
import { imgUrl } from "@/lib/api";
import { Butterfly } from "@/components/site/LogoMark";
import SeoHead from "@/components/site/SeoHead";

const About = () => (
  <div className="pt-24 md:pt-40 pb-24 md:pb-32" data-testid="about-page">
    <SeoHead
      title="Our story · Meghana & Lallitha"
      description="MELAVI began at lunch in Bengaluru. Meghana TR and Lallitha Mukund on slow-fashion, hand-dyed cotton, and clothes made to outlast the child."
      path="/our-story"
    />
    <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-24">
      {/* Intro */}
      <div className="grid md:grid-cols-12 gap-8 md:gap-12 mb-16 md:mb-24">
        <div className="md:col-span-2 flex md:justify-end">
          <Butterfly size={40} />
        </div>
        <div className="md:col-span-9">
          <div className="nav-item mb-3 md:mb-4">The house</div>
          <h1 className="serif text-4xl md:text-7xl leading-[1.02]">
            It began at <em className="italic">lunch</em>.
          </h1>
          <p className="mt-6 md:mt-8 text-base md:text-xl text-ink/80 max-w-[64ch] leading-relaxed">
            Meghana TR and Lallitha Mukund sat down for lunch in Bengaluru one Sunday.
            They talked about the clothes they wished they could put their children in &mdash;
            hand-dyed, hand-embroidered, small enough to know every piece by name.
            By the time the plates were cleared, MELAVI existed.
          </p>
        </div>
      </div>

      {/* Founders — cinematic portrait + caption */}
      <div className="grid md:grid-cols-12 gap-8 md:gap-16 items-center mb-20 md:mb-32" data-testid="about-founders">
        <div className="md:col-span-7 order-2 md:order-1">
          <div className="overflow-hidden">
            <img
              src={imgUrl("/api/uploads/team/founders.webp")}
              alt="Meghana TR and Lallitha Mukund at their rooftop table in Bengaluru"
              className="w-full aspect-[4/3] object-cover"
              data-testid="founders-portrait"
            />
          </div>
          <div className="mt-3 text-[10px] tracking-[0.24em] uppercase text-ink/50">
            Meghana &amp; Lallitha · The rooftop, Bengaluru · An evening in January
          </div>
        </div>
        <div className="md:col-span-5 order-1 md:order-2">
          <div className="nav-item mb-4" style={{ color: "var(--accent)" }}>The founders</div>
          <h2 className="serif text-3xl md:text-5xl leading-[1.05] mb-6">
            Meghana &amp; <em className="italic">Lallitha</em>.
          </h2>
          <p className="text-ink/80 leading-relaxed">
            One of us designs. One of us runs the batches. Both of us have small children who
            outgrew every shirt in the shop before we could love them. We started MELAVI so
            our own children &mdash; and everyone else&rsquo;s &mdash; could have a small, loved wardrobe instead of a big, forgotten one.
          </p>
          <p className="mt-4 text-ink/80 leading-relaxed">
            We meet at this blue table on the rooftop every Tuesday morning, before the workshop
            downstairs wakes up. Two notebooks, one pot of coffee, the sound of Bengaluru finding its feet.
            Everything you own from MELAVI began on this table.
          </p>
          <div className="mt-8 flex flex-wrap items-center gap-6">
            <Link to="/journal" className="crimson-link nav-item" data-testid="about-journal-cta">
              Read the essays →
            </Link>
            <Link to="/the-craft" className="nav-item text-ink/70 hover:text-ink" data-testid="about-craft-inline">
              See the workshop →
            </Link>
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="grid md:grid-cols-12 gap-8 md:gap-12 mb-20 md:mb-24">
        <div className="md:col-span-6 md:col-start-4 space-y-6 md:space-y-8 text-ink/80 leading-relaxed">
          <p>
            We bootstrap. Every rupee that came in went to the next batch. The first order was
            embroidered on a table in Meghana&rsquo;s living room. It still is, on some days.
          </p>
          <p>
            Everything is made in small batches — sometimes twenty pieces, sometimes fifty,
            never more. One butterfly takes eleven hours to stitch. Coconut wood buttons,
            never metal. Side-seam labels, so nothing scratches. Every hem hand-finished.
            No two pieces exactly alike.
          </p>
          <p>
            We built this brand to move slowly, and to stay small enough that slowness stays
            possible. Twinning shirts for the fathers who asked. Women&rsquo;s pieces in the same
            palette as the children&rsquo;s, because families in the same room deserve the same colours.
          </p>
        </div>
      </div>

      <div className="hairline-t hairline-b py-14 md:py-16 grid grid-cols-3 gap-6 md:gap-12" data-testid="about-facts">
        <div>
          <div className="serif text-3xl md:text-5xl">2024</div>
          <div className="nav-item mt-2 md:mt-3">Founded</div>
        </div>
        <div>
          <div className="serif text-3xl md:text-5xl">11h</div>
          <div className="nav-item mt-2 md:mt-3">Per butterfly</div>
        </div>
        <div>
          <div className="serif text-3xl md:text-5xl">100%</div>
          <div className="nav-item mt-2 md:mt-3">Bootstrapped</div>
        </div>
      </div>

      <div className="mt-12 md:mt-16 flex flex-wrap items-center gap-6">
        <Link to="/the-craft" className="btn-ink" data-testid="about-craft-cta">Read The Craft</Link>
        <Link to="/contact-us" className="crimson-link nav-item !text-[12px]">Write to us →</Link>
      </div>
    </div>
  </div>
);

export default About;
